<template>
  <v-container class="pa-4">
    <page-loading
      v-if="loading"
      message="Carregando dados bancários"
      subtitle="Consultando status da conexão PagBank"
      :show-preview="false"
    />
    <template v-else>
      <title-vue title="Dados bancários" />

      <premium-upsell-banner
        v-if="showPremiumUpsell"
        class="mt-4 mb-2"
        variant="compact"
        title="Premium + PagBank = comissões da rede na sua conta"
        subtitle="Torne-se Premium para receber comissões de adesão e conecte o PagBank para sacar."
        cta-label="Aderir ao Premium"
        :show-secondary="false"
        @cta="goToPremiumUpsell"
      />

      <v-card class="mt-4 payment-card" outlined>
      <v-card-title class="text-h6">
        <v-icon left color="#e5183d">mdi-bank-transfer</v-icon>
        Conexão PagBank
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col v-if="adminFranchises.length > 1" cols="12">
            <v-select
              v-model="franchiseId"
              :items="adminFranchises"
              item-text="name"
              item-value="id"
              label="Franquia"
              outlined
              dense
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="8" lg="6">
            <div class="processor-logo-container mb-4">
              <img
                :src="pagBankLogoUrl"
                alt="PagBank Logo"
                class="processor-logo"
                @error="handleLogoFallback"
              />
            </div>
            <h3 class="text-h6 mb-2">PagBank</h3>
            <p class="subtitle-2 text--secondary mb-3">
              {{ pagBankDescription }}
            </p>
            <v-chip
              class="mb-4"
              :color="pagbankConnect ? 'success' : 'error'"
              text-color="white"
              label
            >
              <v-icon left small>
                {{ pagbankConnect ? "mdi-check-circle" : "mdi-alert-circle" }}
              </v-icon>
              {{ pagbankConnect ? "Conectado" : "Não conectado" }}
            </v-chip>
            <v-btn
              color="#00A859"
              dark
              large
              block
              :disabled="!canConnectPagBank"
              @click="showPagBankConnectDialog = true"
            >
              <v-icon left>{{ pagbankConnect ? "mdi-refresh" : "mdi-link" }}</v-icon>
              {{ pagbankConnect ? "Reconectar PagBank" : "Conectar com PagBank" }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <PagBankConnectDialog
      v-if="showConnectDialog"
      v-model="showPagBankConnectDialog"
      :franchise-id="franchiseId"
      :associate-id="associateId"
      :is-franchise-admin="isFranchiseAdmin"
      @connected="handlePagBankConnected"
    />
    </template>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import TitleVue from "@/components/title/Title.vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import PagBankConnectDialog from "@/components/pagbank/PagBankConnectDialog.vue";
import PremiumUpsellBanner from "@/components/premium/PremiumUpsellBanner.vue";
import { GetPagBankStatus } from "@/services/franchises/GetPagBankStatus";
import { SessionFranchiseType } from "@/types/user/SessionInfo";

const getPagBankStatus = new GetPagBankStatus();

export default defineComponent({
  name: "BankDataView",
  components: { TitleVue, PageLoading, PagBankConnectDialog, PremiumUpsellBanner },
  computed: {
    showPremiumUpsell(): boolean {
      return this.$store.getters.shouldShowPremiumUpsell;
    },
    isFranchiseAssociateInactive(): boolean {
      return this.$store.getters.isFranchiseAssociateInactive;
    },
    isFranchiseAdmin(): boolean {
      return this.$store.getters.isCollaboratorManager;
    },
    associateId(): string {
      return this.$store.state.session?.associateId ?? "";
    },
    canConnectPagBank(): boolean {
      if (this.isFranchiseAssociateInactive) {
        return false;
      }
      if (this.isFranchiseAdmin) {
        return !!this.franchiseId;
      }
      return !!this.associateId;
    },
    showConnectDialog(): boolean {
      return this.isFranchiseAdmin ? !!this.franchiseId : !!this.associateId;
    },
    pagBankDescription(): string {
      if (this.isFranchiseAdmin) {
        return "Conecte a conta PagBank da franquia para receber pagamentos diretamente na conta.";
      }
      return "Conecte sua conta PagBank para receber comissões e repasses vinculados ao seu perfil de associado.";
    },
    adminFranchises(): SessionFranchiseType[] {
      return this.$store.getters.sessionFranchises.filter(
        (franchise: SessionFranchiseType) => franchise.isAdmin
      );
    },
  },
  data() {
    return {
      franchiseId: "",
      showPagBankConnectDialog: false,
      pagBankLogoUrl: require("@/assets/pagbank-logo.png"),
      pagbankConnect: false,
      loading: true,
    };
  },
  watch: {
    adminFranchises: {
      immediate: true,
      handler(franchises: SessionFranchiseType[]) {
        if (!this.franchiseId && franchises.length > 0) {
          this.franchiseId = franchises[0].id;
        }
      },
    },
    franchiseId: {
      immediate: true,
      handler() {
        this.loadPagBankStatus();
      },
    },
    associateId: {
      immediate: true,
      handler() {
        if (!this.isFranchiseAdmin) {
          this.loadPagBankStatus();
        }
      },
    },
  },
  methods: {
    goToPremiumUpsell() {
      this.$router.push({ name: "associates", query: { adesao: "premium" } });
    },
    async loadPagBankStatus() {
      this.loading = true;
      try {
        const franchiseId = this.isFranchiseAdmin ? this.franchiseId : undefined;
        if (this.isFranchiseAdmin && !franchiseId) {
          this.pagbankConnect = false;
          return;
        }
        if (!this.isFranchiseAdmin && !this.associateId) {
          this.pagbankConnect = false;
          return;
        }

        const status = await getPagBankStatus.execute(franchiseId || undefined);
        this.pagbankConnect = !!status.pagbankConnect;
      } catch {
        const session = this.$store.state.session;
        if (this.isFranchiseAdmin) {
          const franchise = (session?.franchises ?? []).find(
            (item: SessionFranchiseType) => item.id === this.franchiseId
          );
          this.pagbankConnect = !!franchise?.pagbankConnect;
        } else {
          this.pagbankConnect = !!session?.pagbankConnect;
        }
      } finally {
        this.loading = false;
      }
    },
    async handlePagBankConnected() {
      await this.$store.dispatch("loadSession");
      await this.loadPagBankStatus();
      this.$store.dispatch("showSnack", {
        message: this.isFranchiseAdmin
          ? "Conta PagBank da franquia conectada com sucesso."
          : "Conta PagBank conectada com sucesso.",
        color: "green",
      });
    },
    handleLogoFallback(event: Event) {
      const img = event.target as HTMLImageElement;
      img.src =
        'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 200 60%22%3E%3Ctext x=%2210%22 y=%2235%22 font-size=%2220%22 font-weight=%22bold%22 fill=%22%2300A859%22%3EPagBank%3C/text%3E%3C/svg%3E';
    },
  },
});
</script>

<style scoped>
.payment-card {
  border-radius: 16px;
  border: 1px solid #e0e0e0;
}

.processor-logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
}

.processor-logo {
  max-width: 150px;
  max-height: 60px;
  object-fit: contain;
}
</style>
